/**
 * Frontend structured logger for Logz.io.
 * Follows Powtoon conventions: factory import, module-scoped loggers,
 * dual local+remote logging, sensitive data redaction, memory metrics.
 *
 * Sentry bridge:
 * - error → Sentry.captureException / captureMessage
 * - warn/info → Sentry.addBreadcrumb (trail leading up to errors)
 */

const APP_NAME = "live-avatar-front";
const APP_VERSION = "1.0.0";
const MAX_ARGS_LENGTH = 1500;

const LOG_LEVELS = ["debug", "info", "warn", "error"] as const;
type LogLevel = (typeof LOG_LEVELS)[number];

// ─── Hostname-based env detection (kept local to avoid sentry.ts cycle) ─────

function detectAppLevel(): string {
  if (typeof window === "undefined") return "server";
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "development";
  if (host === "live-ai-avatars.powtoon.com") return "powtoon-production";
  if (host === "live-ai-avatars-staging.powtoon.com") return "powtoon-staging";
  if (host === "live-ai-avatars-dev.powtoon.com") return "powtoon-dev";
  if (host.endsWith(".lovable.app")) return "lovable-preview";
  return "unknown";
}

const APP_LEVEL = detectAppLevel();

// ─── Shared module-level state ──────────────────────────────────────────────

export const tabId = crypto.randomUUID();
const appStartTime = Date.now();

let powtoonUserId: string | number = "anonymous";
let requestUserName: string | null = null;

export function setLoggerIdentity(userId: string | number, userName?: string): void {
  powtoonUserId = userId;
  if (userName) requestUserName = userName;
}

export function clearLoggerIdentity(): void {
  powtoonUserId = "anonymous";
  requestUserName = null;
}

// ─── Sensitive data redaction ───────────────────────────────────────────────

const SENSITIVE_KEYS = new Set([
  "access_token",
  "refresh_token",
  "client_secret",
  "service_role_key",
  "authorization",
  "token",
  "password",
  "secret",
  "jwt",
  "id_token",
  "bearer",
  "apikey",
  "api_key",
]);

function redact(data: unknown, depth = 0): unknown {
  if (depth > 5 || data == null) return data;
  if (typeof data === "string") return data.startsWith("eyJ") ? "[REDACTED_JWT]" : data;
  if (Array.isArray(data)) return data.map((v) => redact(v, depth + 1));
  if (typeof data === "object") {
    const out: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(data as Record<string, unknown>)) {
      if (SENSITIVE_KEYS.has(key.toLowerCase())) {
        out[key] = "[REDACTED]";
      } else {
        out[key] = redact(val, depth + 1);
      }
    }
    return out;
  }
  return data;
}

// ─── Memory metrics (throttled) ─────────────────────────────────────────────

let lastMemoryCapture = 0;
let cachedMemory: Record<string, number> | null = null;

function getMemoryMetrics(): Record<string, number> | null {
  const perf = performance as unknown as {
    memory?: { jsHeapSizeLimit: number; totalJSHeapSize: number; usedJSHeapSize: number };
  };
  if (!perf.memory) return null;

  const now = Date.now();
  if (now - lastMemoryCapture < 1000 && cachedMemory) return cachedMemory;

  lastMemoryCapture = now;
  cachedMemory = {
    memoryJsHeapBytesLimit: perf.memory.jsHeapSizeLimit,
    memoryJSHeapBytesAllocated: perf.memory.totalJSHeapSize,
    memoryJSHeapBytesInUse: perf.memory.usedJSHeapSize,
  };
  return cachedMemory;
}

// ─── Sentry bridge helpers ──────────────────────────────────────────────────

function addSentryBreadcrumb(
  message: string,
  level: "debug" | "info" | "warning",
  category: string,
  data?: Record<string, unknown>,
): void {
  try {
    import("@sentry/react").then((Sentry) => {
      Sentry.addBreadcrumb({ message, level, category, data });
    });
  } catch {
    // Sentry may not be initialized — silently ignore
  }
}

function captureSentryError(
  message: string,
  data?: Record<string, unknown>,
  moduleName?: string,
): void {
  try {
    import("@sentry/react").then((Sentry) => {
      if (data?.error instanceof Error) {
        Sentry.captureException(data.error, {
          extra: { ...data, moduleName, message },
        });
      } else {
        Sentry.captureMessage(message, {
          level: "error",
          extra: { ...data, moduleName },
        });
      }
    });
  } catch {
    // Sentry may not be initialized
  }
}

// ─── Logz.io HTTP transport ─────────────────────────────────────────────────

class LogzioClient {
  private endpoint: string;

  constructor(supabaseUrl: string) {
    this.endpoint = `${supabaseUrl}/functions/v1/frontend-log`;
  }

  send(payload: Record<string, unknown>): void {
    const body = JSON.stringify(payload);
    const correlationId = `${tabId.slice(0, 8)}-${crypto.randomUUID().slice(0, 8)}`;
    fetch(this.endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-correlation-id": correlationId,
      },
      body,
      keepalive: true,
    })
      .then((res) => {
        if (import.meta.env.DEV)
          console.log(`[LogzioClient] ${res.ok ? "✓" : "✗"} ${res.status} | "${payload.message}"`);
      })
      .catch((err) => {
        if (import.meta.env.DEV)
          console.warn(`[LogzioClient] failed: ${err.message} | "${payload.message}"`);
      });
  }
}

const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? null;
const logzioClient = SUPABASE_URL ? new LogzioClient(SUPABASE_URL) : null;

// ─── Logger class ───────────────────────────────────────────────────────────

class Logger {
  private moduleName: string;
  private minLevel: number;

  constructor(moduleName: string, minLevel: LogLevel = "info") {
    this.moduleName = moduleName;
    this.minLevel = LOG_LEVELS.indexOf(minLevel);
  }

  debug(message: string, data?: Record<string, unknown>): void {
    this.log("debug", message, data);
  }

  info(message: string, data?: Record<string, unknown>): void {
    this.log("info", message, data);
    addSentryBreadcrumb(message, "info", `${APP_NAME}.${this.moduleName}`, data);
  }

  warn(message: string, data?: Record<string, unknown>): void {
    this.log("warn", message, data);
    addSentryBreadcrumb(message, "warning", `${APP_NAME}.${this.moduleName}`, data);
  }

  error(message: string, data?: Record<string, unknown>): void {
    this.log("error", message, data);
    captureSentryError(message, data, this.moduleName);
  }

  metric(title: string, metrics: Record<string, unknown>): void {
    this.log("info", title, { ...metrics, powtoonMetricId: "operation_metrics" });
  }

  private log(level: LogLevel, message: string, data?: Record<string, unknown>): void {
    const levelIndex = LOG_LEVELS.indexOf(level);
    if (levelIndex < this.minLevel) return;

    if (import.meta.env.DEV) {
      const consoleMethod =
        level === "error"
          ? console.error
          : level === "warn"
            ? console.warn
            : level === "debug"
              ? console.debug
              : console.log;
      consoleMethod(`[${this.moduleName}] ${message}`, data ?? "");
    }

    if (!logzioClient) return;

    const redactedData = data ? (redact(data) as Record<string, unknown>) : undefined;
    let args: string | undefined;
    if (redactedData) {
      try {
        const raw = JSON.stringify(redactedData);
        args = raw.length > MAX_ARGS_LENGTH ? raw.substring(0, MAX_ARGS_LENGTH) : raw;
      } catch {
        args = "[unserializable]";
      }
    }

    const payload: Record<string, unknown> = {
      "@timestamp": new Date().toISOString(),
      level: level.toUpperCase(),
      message,
      logger: `${APP_NAME}.${this.moduleName}`,
      logzio_type: APP_NAME,
      appName: APP_NAME,
      appVersion: APP_VERSION,
      appLevel: APP_LEVEL,
      moduleName: this.moduleName,
      tabId,
      powtoonUserId,
      uptime: Date.now() - appStartTime,
      domain: typeof window !== "undefined" ? window.location.host : "unknown",
    };

    if (requestUserName) payload.requestUserName = requestUserName;
    if (args) payload.args = args;

    if (redactedData) {
      for (const [key, val] of Object.entries(redactedData)) {
        if (!(key in payload)) payload[key] = val;
      }
    }

    const mem = getMemoryMetrics();
    if (mem) Object.assign(payload, mem);

    logzioClient.send(payload);
  }
}

export function createLogger(moduleName: string): Logger {
  return new Logger(moduleName);
}

if (logzioClient) {
  const startupLogger = new Logger("Startup");
  startupLogger.info("Frontend logger initialized", {
    endpoint: SUPABASE_URL ? `${SUPABASE_URL}/functions/v1/frontend-log` : "missing",
    url: typeof window !== "undefined" ? window.location.href : "unknown",
  });
} else if (import.meta.env.DEV) {
  console.warn("[LogzioClient] No VITE_SUPABASE_URL — remote logging disabled");
}